import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createInvoice, fetchPaymentHistory, recordPayment } from '../lib/bridge';
import { Button } from '../components/ui/Button';
import { ErrorState } from '../components/ui/ErrorState';
import { LoadingState } from '../components/ui/LoadingState';
import { Card, CardHeader } from '../components/ui/Card';
import { fieldControlSmClass, fieldLabelClass } from '../components/ui/forms';

// Customer / Payment section of the job screen. Creating an invoice and
// recording a payment are Owner/Admin/Manager-only writes (JobDetailPage
// only renders this for MANAGEMENT_ROLES) that go straight through the
// unmodified appCreateInvoiceFromEstimate/appRecordPayment — see the
// comment on `billingCreateInvoice` in src/V6Api.js for the full list of
// what stays enforced server-side (work-complete gating, balance checks,
// the document lock).
export function BillingPanel({ documentNo, stage, totalText, canWrite }: { documentNo: string; stage?: string; totalText?: string; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const historyQuery = useQuery({ queryKey: ['paymentHistory', documentNo], queryFn: () => fetchPaymentHistory(documentNo), enabled: !!documentNo });

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Check');
  const [reference, setReference] = useState('');

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['paymentHistory', documentNo] });
    void queryClient.invalidateQueries({ queryKey: ['jobDetail'] });
  }

  const invoiceMutation = useMutation({
    mutationFn: () => createInvoice(documentNo),
    onSuccess: (result) => result.ok && invalidate(),
  });

  const paymentMutation = useMutation({
    mutationFn: () => recordPayment({ documentNo, amount: Number(amount || 0), method, reference }),
    onSuccess: (result) => {
      if (result.ok) {
        setAmount('');
        setReference('');
        invalidate();
      }
    },
  });

  return (
    <Card>
      <CardHeader title="Customer / Payment" subtitle={`Job total: ${totalText ?? '—'} · Status: ${stage ?? '—'}`} />

      {canWrite && stage === 'Estimate' && (
        <div className="mb-3">
          <Button variant="secondary" busy={invoiceMutation.isPending} busyLabel="Creating…" onClick={() => invoiceMutation.mutate()}>
            Create Invoice
          </Button>
          {invoiceMutation.data && !invoiceMutation.data.ok && <ErrorState message={invoiceMutation.data.message || 'Could not create the invoice.'} />}
        </div>
      )}

      {canWrite && (stage === 'Invoice' || stage === 'Paid') && (
        <form
          className="mb-3 flex flex-wrap items-end gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (Number(amount) > 0) paymentMutation.mutate();
          }}
        >
          <div className="w-28 space-y-1">
            <label htmlFor="pay-amount" className={fieldLabelClass}>
              Amount
            </label>
            <input
              id="pay-amount"
              type="number"
              min="0.01"
              step="0.01"
              className={fieldControlSmClass}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="pay-method" className={fieldLabelClass}>
              Method
            </label>
            <select id="pay-method" className={fieldControlSmClass} value={method} onChange={(e) => setMethod(e.target.value)}>
              {['Check', 'Cash', 'Credit Card', 'Bank Transfer', 'Other'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label htmlFor="pay-ref" className={fieldLabelClass}>
              Reference
            </label>
            <input id="pay-ref" className={fieldControlSmClass} value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <Button type="submit" busy={paymentMutation.isPending} busyLabel="Recording…">
            Record Payment
          </Button>
        </form>
      )}
      {paymentMutation.data && !paymentMutation.data.ok && <ErrorState message={paymentMutation.data.message || 'Could not record this payment.'} />}

      {historyQuery.isLoading && <LoadingState label="Loading payment history…" />}
      {historyQuery.data?.ok && (historyQuery.data.payments?.length ?? 0) > 0 && (
        <ul className="divide-y divide-ink-border">
          {historyQuery.data.payments!.map((p) => (
            <li key={p.paymentId} className="flex items-center justify-between py-2.5 text-sm">
              <span className="text-slate-300">
                {p.date} · {p.method}
                {p.reference ? ` (${p.reference})` : ''}
              </span>
              <span className="font-semibold text-gold-200">{p.amountText}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
